








































































































import Vue from 'vue';
import gql from 'graphql-tag';
import * as CSV from 'csv-string';
import { mapActions, mapGetters } from 'vuex';
import { forEach, map, assign, includes, flow, sortBy, find, zipObject, remove, filter, compact, clone } from 'lodash';
import { BFormTextarea } from 'bootstrap-vue';
import ZenMultiSelect from '@reciprocity/multiselect';
import ZenButton from '@reciprocity/button';
import ZenTable from '@/components/listing/ZenTable.vue';
import {
  Column,
  DataRow,
  DropdownOption,
  ERROR_NO_DATA,
  ERROR_OBJECT_TYPE_NOT_SELECTED,
  ERROR_ATTRIBUTES_NOT_SELECTED,
  ERROR_IMPORTING_MULTIPLE_OBJECTS,
  ERROR_MAX_ROW_LIMIT,
} from '../types';
import {
  attributeToDropdownOption,
  attributeToDropdownOptionFromName,
  objectTypeToDropdownOption,
  createUnmapDropdownOption,
  AswObjectTypes,
  SPECIAL_ATTRIBUTES,
  OBJECT_TYPE_TO_REMOVE,
  JIRA_OBJECT_TYPE,
  SNOW_OBJECT_TYPE,
  SNOW_ATTRIBUTES,
  JIRA_ATTRIBUTES,
} from '../helper';

export default Vue.extend({
  name: 'ClipboardImport',
  components: {
    BFormTextarea,
    ZenTable,
    ZenButton,
    ZenMultiSelect,
  },
  props: {
    showDataPreview: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
      default: true,
    },
    maxRows: {
      type: Number,
      default: 1000,
    },
    allowedObjectType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      pastedData: <string | null>null,
      parsedData: [] as Array<string>,
      delimiter: <string>'\t',
      columns: [] as Array<Column>,
      rows: [] as Array<DataRow>,
      selectedObjectType: <DropdownOption | null>null,
      attributes: [] as Array<object>,
      selectedObjects: <string[]>[],
      hasErrors: <boolean>false,
    };
  },
  computed: {
    ...mapGetters(['importableObjects']),
    isValid(): boolean | null {
      return this.pastedData ? !this.hasErrors && this.pastedData.includes(this.delimiter) : null;
    },
    isValidPastedData(): boolean {
      if (this.hasMultipleObjectTypes) {
        this.showErrorMessages(ERROR_IMPORTING_MULTIPLE_OBJECTS);
        return false;
      } else if (this.hasMaxRows) {
        this.showErrorMessages(ERROR_MAX_ROW_LIMIT);
        return false;
      } else {
        return true;
      }
    },
    isValidImportData(): boolean {
      let isValid = true;

      if (this.rows.length == 0) {
        this.showErrorMessages(ERROR_NO_DATA);
        isValid = false;
      }
      if (!this.selectedObjectType) {
        this.showErrorMessages(ERROR_OBJECT_TYPE_NOT_SELECTED);
        isValid = false;
      }
      if (!this.hasAllAttributesSelected()) {
        this.showErrorMessages(ERROR_ATTRIBUTES_NOT_SELECTED);
        isValid = false;
      }
      return isValid;
    },
    computedObjectOptions(): DropdownOption[] {
      let objectOptions = [];
      const allowedObjectType = this.allowedObjectType;

      // Add allowed object types for ASW import
      // TODO This implementation has to be removed when jira and snow are implemented correctly!!!
      switch (allowedObjectType) {
        case AswObjectTypes.JIRA: {
          objectOptions = JIRA_OBJECT_TYPE;
          break;
        }
        case AswObjectTypes.SNOW: {
          objectOptions = SNOW_OBJECT_TYPE;
          break;
        }
        default:
          objectOptions = this.importableObjects;
          if (allowedObjectType) objectOptions = filter(objectOptions, obj => allowedObjectType == obj.titleSingular);
      }

      // Filter out not wanted object types
      objectOptions = filter(
        objectOptions,
        obj => !OBJECT_TYPE_TO_REMOVE.some(remove => obj.titleSingular.includes(remove))
      );

      // Map filtered to DropdownOption object
      return map(objectOptions, objectType => objectTypeToDropdownOption(objectType, false));
    },
    computedAttributeOptions(): DropdownOption[] {
      let allAttributes = [];
      const allowedObjectType = this.allowedObjectType;
      let attributeOptions = new Array<DropdownOption>();
      let activeAttributes = compact(map(this.columns, 'activeAttribute'));

      // Add allowed attributes for ASW import
      // TODO This implementation has to be removed when jira and snow are implemented correctly!!!
      switch (allowedObjectType) {
        case AswObjectTypes.JIRA: {
          allAttributes = JIRA_ATTRIBUTES;
          break;
        }
        case AswObjectTypes.SNOW: {
          allAttributes = SNOW_ATTRIBUTES;
          break;
        }
        default: {
          allAttributes = this.attributes;
          break;
        }
      }

      // Set disabled and map to dropdownOption object
      forEach(allAttributes, attr => {
        let disabled = !!find(activeAttributes, activeAttr => includes(activeAttr.displayName, attr.displayName));
        let dropdownOption = attributeToDropdownOption(attr, disabled);

        // Add unmapping attributes
        if (attr.isMapping && attr.name != 'mapped_controls') {
          attributeOptions.push(createUnmapDropdownOption(dropdownOption));
        }

        // Add attribute
        attributeOptions.push(dropdownOption);
      });

      // Add special attributes
      forEach(SPECIAL_ATTRIBUTES, attrName => {
        attributeOptions.push(attributeToDropdownOptionFromName(attrName, false));
      });

      return attributeOptions;
    },
    groupedAttributeOptions() {
      let attributes = this.computedAttributeOptions;
      let mandatoryAttr = new Array<DropdownOption>();
      let customAttr = new Array<DropdownOption>();
      let mappingAttr = new Array<DropdownOption>();
      let regularAttr = new Array<DropdownOption>();

      forEach(attributes, attribute => {
        if (attribute.mandatory) {
          mandatoryAttr.push(attribute);
        } else if (attribute.isCustomAttribute) {
          customAttr.push(attribute);
        } else if (attribute.isMapping) {
          mappingAttr.push(attribute);
        } else {
          regularAttr.push(attribute);
        }
      });

      return [
        {
          attributesGroup: 'MANDATORY',
          attributes: sortBy(mandatoryAttr, ['displayName']),
        },
        {
          attributesGroup: 'REGULAR',
          attributes: sortBy(regularAttr, ['displayName']),
        },
        {
          attributesGroup: 'CUSTOM',
          attributes: sortBy(customAttr, ['displayName']),
        },
        {
          attributesGroup: 'MAPPINGS',
          attributes: sortBy(mappingAttr, ['displayName']),
        },
      ];
    },
    columnsCount(): number {
      return this.columns.length;
    },
    canDeleteRows(): boolean {
      return this.selectedObjects.length > 0;
    },
    hasMaxRows(): boolean {
      return this.parsedData.length > this.maxRows;
    },
    hasMultipleObjectTypes(): boolean {
      return this.headerRowCount() > 1;
    },
    hasHeader(): boolean {
      return this.headerRowCount() > 0;
    },
  },
  methods: {
    ...mapActions({
      showAlert: 'alerts/show',
    }),
    dataPreviewToggle(state: boolean): void {
      this.$emit('dataPreviewToggle', state);
    },
    importBtnToggle(): void {
      this.$emit('importBtnToggle', !this.hasAllAttributesSelected());
    },
    pasteData(inputData: string) {
      this.hasErrors = false;
      if (!this.isValid) {
        return false;
      }

      this.parsedData = CSV.parse(inputData, this.delimiter);
      if (!this.isValidPastedData) {
        this.hasErrors = true;
        return false;
      }

      this.parseRows();
      this.preSelectObjectType();
      this.dataPreviewToggle(true);
    },
    parseRows(): void {
      let hasHeader = this.hasHeader;
      let rowsParsed = this.parsedData;
      let columns = new Array<Column>();
      let rows = new Array<DataRow>();
      let columnRow = rowsParsed[0];

      if (hasHeader) {
        columnRow = rowsParsed[1];
        this.selectedObjectType = find(this.computedObjectOptions, objectType =>
          includes(rowsParsed[1][0], objectType.id)
        );

        // cleanup data (header rows, object column)
        if (columnRow) {
          columnRow.shift();
          rowsParsed.splice(0, 2);
          forEach(rowsParsed, row => {
            return row.shift();
          });
        }
      }

      // remove empty columns
      const emptyColumns = (rowsParsed[0] || []).map((c, i) => rowsParsed.some(a => a[i]));
      columnRow = columnRow.filter((_, i) => emptyColumns[i]);
      rowsParsed = rowsParsed.map(row => row.filter((_, i) => emptyColumns[i]));

      // create column objects for table ({key:0, label{Title}})
      forEach(columnRow, (value, index) => {
        let column = <Column>{};
        column.key = hasHeader ? value : index.toString();
        column.label = hasHeader ? value : index.toString();
        columns.push(column);
      });

      // create row objects for table ({id:0, code : value{Code}})
      forEach(rowsParsed, (row, index) => {
        let dataRow = <DataRow>{};
        dataRow.id = index;

        let valueRow = map(row, singleRow => {
          return { value: singleRow };
        });

        let resultRow = hasHeader ? zipObject(columnRow, valueRow) : valueRow;
        assign(dataRow, resultRow);
        rows.push(dataRow);
      });

      this.columns = columns;
      this.rows = rows;
    },
    preSelectObjectType() {
      if (this.computedObjectOptions.length == 1) {
        this.selectedObjectType = this.computedObjectOptions[0];
      }
    },
    resetInputData(): void {
      this.pastedData = null;
    },
    resetPage(): void {
      this.$emit('resetPage', true);
    },
    reset(): void {
      this.pastedData = null;
      this.parsedData = '';
      this.hasErrors = false;
      this.columns = null;
      this.rows = null;
      this.selectedObjectType = null;
      this.selectedObjects = [];
    },
    headerRowCount(): number {
      let headerCount = 0;
      forEach(this.parsedData, row => {
        if (row[0].includes('Object type')) {
          headerCount++;
        }
      });
      return headerCount;
    },
    getColumnMatch(column: Column): Column | null {
      const attribute = find(this.computedAttributeOptions, attr => column.label.includes(attr.displayName));
      if (attribute) {
        attribute.$isDisabled = true;
        column.label = attribute.label;
        column.activeAttribute = attribute;
      }
      this.importBtnToggle();
      return attribute ? column : null;
    },
    onAttributeChange(column: Column, selected: DropdownOption): Column {
      column.label = selected.label;
      column.activeAttribute = selected;

      // We have to trigger computedAttributeOptions so it disables the selected!
      this.columns = clone(this.columns);
      return column;
    },
    onSelectedRowChange(rows: string[]) {
      this.$emit('selectedRowChange', rows);
      this.selectedObjects = rows;
    },
    resetSelectedObjects() {
      this.selectedObjects = [];
    },
    onSelectedRowDelete() {
      remove(this.rows, row => this.selectedObjects.includes(row.id));
      this.resetSelectedObjects();
    },
    hasAllAttributesSelected() {
      let allSelected = true;
      forEach(this.columns, col => {
        if (col && col.activeAttribute == undefined) {
          allSelected = false;
        }
      });
      return allSelected;
    },
    showErrorMessages(message: string): void {
      this.showAlert({
        type: 'error',
        message: message,
      });
    },
    getImportCSV(): string | boolean {
      let mainHeader = [];
      let header = [];
      let rows = [];

      if (!this.isValidImportData) return false;

      // Create main header. Example ('Object Type','','','','')
      mainHeader = new Array(this.columnsCount).fill('');
      mainHeader.unshift('Object type');

      forEach(this.columns, col => {
        if (col && col.activeAttribute && col.activeAttribute !== undefined) {
          header.push(col.activeAttribute.displayName);
        }
      });

      header.unshift(this.selectedObjectType.id);

      // Get all values from array and add prepend an empty column
      // TODO: This whole data structure needs to be refactored to keep the order
      forEach(this.rows, row => {
        const keys = Object.keys(row);
        keys.splice(keys.indexOf('id'), 1);
        keys.unshift('id');
        let rowValues = keys.map(key => {
          return key == 'id' ? '' : row[key].value;
        });

        rows.push(rowValues);
      });

      rows.unshift(header);
      rows.unshift(mainHeader);

      this.pastedData = '';
      // Create a comma delimited data
      return CSV.stringify(rows);
    },
  },
  apollo: {
    attributes: {
      query: gql`
        query Attributes($objectType: SearchType!) {
          objectType(name: $objectType) {
            attrs {
              name
              displayName
              mandatory
              isMapping
              isCustomAttribute
              isImportable
            }
          }
        }
      `,
      variables() {
        return {
          objectType: this.selectedObjectType.id,
        };
      },
      update(data) {
        return flow(
          data => data.objectType.attrs,
          data => filter(data, attribute => attribute.isImportable),
          data => sortBy(data, attribute => attribute.displayName)
        )(data);
      },
      skip() {
        return (
          !this.selectedObjectType ||
          this.allowedObjectType == AswObjectTypes.JIRA ||
          this.allowedObjectType == AswObjectTypes.SNOW
        );
      },
    },
  },
});
